import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Input, Row, Col, Spin, Typography } from 'antd';
import { apiConfig } from '../authConfig';
import { DASHBOARD_CATEGORY_LABELS, formatDdMmYyyy } from '../constants/dashboardTiles';
import { normaliseDashboardBranding } from '../utils/dashboardBranding';
import DashboardTile from '../components/DashboardTile';
import DashboardBrandHeader from '../components/DashboardBrandHeader';

const { Title, Paragraph } = Typography;

/**
 * Public, unauthenticated shareable-dashboard snapshot page — no MSAL, no AppContext, no
 * `useApp()`. Mounted directly by App.jsx:AppInner for the `/shared/:token` URL pattern, BEFORE
 * any auth branch (per .claude/design/dashboard-redesign.md "Frontend additions": "rendered
 * without the app's MSAL-gated shell"). This file must never import anything that assumes a
 * signed-in MSAL account.
 *
 * Cites dashboard-redesign.md §A.1 (snapshot pinning — freeze at creation), §A.2 (branding SAS
 * chokepoint), §A.5 (AI trust-decay on the public surface).
 *
 * HARD BUILD CONDITION #1 (2nd Devil's Advocate pass): tells an external viewer, in DD-MM-YYYY
 * (Commandment XII), that this snapshot is pinned and not live. It never claims one single "data as
 * of" date, because each tile resolves its own snapshot at or before the link was created: when every
 * value-bearing tile carries its own `computedAt` the tiles show their own dates; otherwise the line
 * says "Link created {date}; tiles show the latest snapshot at that time".
 *
 * HARD BUILD CONDITION #3: every `ai_synthesized` tile's `confidence` + `aiReviewLabel` reach
 * display here exactly as they do in-app — enforced by reusing DashboardTile.jsx verbatim (see
 * that file's header) rather than a second, divergent tile renderer for the public page.
 *
 * Branding (Commandment VIII): renders `data.branding.logoUrl` EXACTLY as returned by the API —
 * per §A.2 this is a short-lived SAS URL minted SERVER-SIDE on the anonymous viewer's behalf.
 * This file deliberately imports neither `durableBlobUrlIfAzure` nor `resolveCompanyLogoUrl`
 * (the resolved-URL fallback pattern used for AUTHENTICATED views elsewhere in this app) — the
 * design doc is explicit that this public route's SAS resolution is server-side by design, not a
 * frontend concern, unlike every other blob-URL consumer in this codebase.
 *
 * Response envelope: `{ success, data: { projectName, companyName, pinnedAsOf, expiresAt,
 * branding: { logoUrl, primaryColor? }, tiles: [...] } }`. Password-protected links are retried with
 * an `X-Shared-Link-Password` header (never a query string, to keep it out of access logs); a
 * missing/incorrect password is HTTP 401 with `error.code === 'PASSWORD_REQUIRED'`, an expired or
 * revoked link is 410, rate limiting is 429.
 */

// Shared links only ever carry 'available' or 'withheld'. Whatever else arrives (a staff-only state
// must never reach this surface) is shown as the client wording, not as an internal workflow reason.
function clientSafeTile(t) {
  if (!t || !t.availability || t.availability === 'available' || t.availability === 'withheld') return t;
  return { ...t, availability: 'withheld' };
}

function formatTilesByCategory(tiles) {
  return tiles.reduce((acc, t) => {
    const cat = t.category || 'other';
    (acc[cat] = acc[cat] || []).push(t);
    return acc;
  }, {});
}

const SharedDashboardPage = ({ token }) => {
  // 'loading' | 'password' | 'error' | 'expired' | 'rateLimited' | 'ready'
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [data, setData] = useState(null);
  const [retryMinutes, setRetryMinutes] = useState(null);

  const load = useCallback(
    async (pwd) => {
      setStatus('loading');
      setErrorMessage(null);
      try {
        const headers = {};
        if (pwd) headers['X-Shared-Link-Password'] = pwd;
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/shared/${token}/data`, { headers });
        const json = await res.json().catch(() => null);

        if (res.status === 410) {
          setStatus('expired');
          return;
        }
        if (res.status === 429) {
          // Password lockout sends Retry-After (seconds); fall back to the body's copy of it.
          const headerSec = Number(res.headers?.get?.('Retry-After'));
          const bodySec = Number(json?.error?.details?.retryAfterSeconds);
          const sec = Number.isFinite(headerSec) && headerSec > 0 ? headerSec : Number.isFinite(bodySec) && bodySec > 0 ? bodySec : null;
          setRetryMinutes(sec ? Math.max(1, Math.ceil(sec / 60)) : null);
          setStatus('rateLimited');
          return;
        }
        if (res.status === 401 && json?.error?.code === 'PASSWORD_REQUIRED') {
          setStatus('password');
          if (pwd) setErrorMessage('Incorrect password - try again.');
          return;
        }
        if (!res.ok || !json?.success) {
          throw new Error(json?.error?.message || `This shared dashboard is not available (HTTP ${res.status}).`);
        }
        setData(json.data || {});
        setStatus('ready');
      } catch (err) {
        setErrorMessage(err.message || 'Could not load this shared dashboard.');
        setStatus('error');
      }
    },
    [token],
  );

  useEffect(() => {
    if (token) load();
    // NOTE: the exhaustive-deps hooks rule is not loaded by this build's ESLint config (see
    // .claude/lessons/eslint-plugin-react-hooks-breaks-cra-build.md) — plain-English note in
    // place of a real suppression comment. Runs once per token (a fresh page load always carries
    // a fresh token; the password retry path calls `load` directly, not via this effect).
  }, [token]);

  const handlePasswordSubmit = () => load(passwordInput);

  if (!token) {
    return <Empty style={{ marginTop: 80 }} description="No shared dashboard link provided." />;
  }

  if (status === 'loading') {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (status === 'password') {
    return (
      <div style={{ maxWidth: 420, margin: '120px auto', textAlign: 'center' }}>
        <Title level={4}>This dashboard is password protected</Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input
            type="password"
            placeholder="Enter password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
          />
          <Button type="primary" onClick={handlePasswordSubmit}>
            View dashboard
          </Button>
        </div>
        {errorMessage && <Alert style={{ marginTop: 16 }} type="error" showIcon message={errorMessage} />}
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div style={{ maxWidth: 480, margin: '120px auto' }}>
        <Alert
          type="warning"
          showIcon
          message="This shared dashboard link is no longer available."
          description="It may have expired or been withdrawn. Ask the project's Company SPOC for a new link."
        />
      </div>
    );
  }

  if (status === 'rateLimited') {
    return (
      <div style={{ maxWidth: 480, margin: '120px auto' }}>
        {retryMinutes ? (
          <Alert
            type="warning"
            showIcon
            data-testid="rate-limit-lockout"
            message={`Too many attempts; try again in ${retryMinutes} ${retryMinutes === 1 ? 'minute' : 'minutes'}.`}
          />
        ) : (
          <Alert type="warning" showIcon message="Too many requests." description="Please wait a moment and reload this page." />
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ maxWidth: 480, margin: '120px auto' }}>
        <Alert type="error" showIcon message="This shared dashboard is not available" description={errorMessage} />
      </div>
    );
  }

  // status === 'ready'
  const tiles = Array.isArray(data?.tiles) ? data.tiles.map(clientSafeTile) : [];
  const byCategory = formatTilesByCategory(tiles);
  const branding = normaliseDashboardBranding(data?.branding);
  // Tiles that carry a value; a withheld tile has no snapshot and so no date to show.
  const valued = tiles.filter((t) => !t.availability || t.availability === 'available');
  const everyTileDated = valued.length > 0 && valued.every((t) => !!t.computedAt);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 16px' }}>
      <div style={{ marginBottom: 8 }}>
        <DashboardBrandHeader
          branding={branding}
          title={data?.companyName || data?.projectName || 'Project dashboard'}
          subtitle={data?.companyName && data?.projectName ? data.projectName : null}
        />
      </div>

      {/* HARD BUILD CONDITION #1: honest freshness statement, DD-MM-YYYY. */}
      <Paragraph data-testid="data-as-of" style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>
        {everyTileDated
          ? 'Each tile shows the date its data was calculated.'
          : `Link created ${formatDdMmYyyy(data?.pinnedAsOf)}; tiles show the latest snapshot at that time.`}{' '}
        This snapshot does not update live. Link expires {formatDdMmYyyy(data?.expiresAt)}.
      </Paragraph>

      {tiles.length === 0 ? (
        <Empty description="No tiles were shared on this link." />
      ) : (
        Object.entries(byCategory).map(([cat, catTiles]) => (
          <div key={cat} style={{ marginBottom: 24 }}>
            <Title level={4}>{DASHBOARD_CATEGORY_LABELS[cat] || cat}</Title>
            <Row gutter={[16, 16]}>
              {catTiles.map((tile) => (
                <Col key={tile.tileId} xs={24} sm={12} lg={8}>
                  <DashboardTile tile={tile} branding={branding} />
                </Col>
              ))}
            </Row>
          </div>
        ))
      )}
    </div>
  );
};

export default SharedDashboardPage;
